// useTelegramWebApp.js - Custom hook for Telegram WebApp functionality
import { useEffect, useState } from 'react';

export const useTelegramWebApp = () => {
  const [webApp, setWebApp] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    if (window.Telegram && window.Telegram.WebApp) {
      const tgWebApp = window.Telegram.WebApp;
      setWebApp(tgWebApp);
      
      // Initialize and expand
      tgWebApp.ready();
        try {
        // Expand to full viewport
        tgWebApp.expand();
        setIsExpanded(true);
        
        // Request fullscreen mode if supported
        if (tgWebApp.requestFullscreen && typeof tgWebApp.requestFullscreen === 'function') {
          tgWebApp.requestFullscreen();
          if (process.env.NODE_ENV === 'development') {
            console.log('🖥️ Fullscreen mode requested');
          }
        }
        
        // Set initial viewport height and fullscreen state
        setViewportHeight(tgWebApp.viewportHeight || window.innerHeight);
        setIsFullscreen(tgWebApp.isFullscreen || false);
        
        // Set theme
        setTheme(tgWebApp.colorScheme || 'light');
        
        // Configure WebApp settings
        tgWebApp.enableClosingConfirmation();
        tgWebApp.setHeaderColor('#ffffff');
        
        // Hide default buttons
        tgWebApp.MainButton.hide();
        tgWebApp.BackButton.hide();
        
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Telegram WebApp initialized and expanded');
          console.log('📏 Viewport Height:', tgWebApp.viewportHeight);
          console.log('🖥️ Fullscreen Mode:', tgWebApp.isFullscreen);
          console.log('🎨 Theme:', tgWebApp.colorScheme);
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize Telegram WebApp:', error);
      }        // Listen for viewport changes (throttled to prevent performance issues)
      let viewportChangeTimeout;
      let themeChangeTimeout;
      
      const handleViewportChanged = () => {
        // Throttle viewport changes to prevent excessive re-renders
        clearTimeout(viewportChangeTimeout);
        viewportChangeTimeout = setTimeout(() => {
          const newHeight = tgWebApp.viewportHeight || window.innerHeight;
          const newFullscreen = tgWebApp.isFullscreen || false;
          
          setViewportHeight(newHeight);
          setIsFullscreen(newFullscreen);
          
          // Update CSS custom property for dynamic height
          document.documentElement.style.setProperty('--tg-viewport-height', `${newHeight}px`);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('📏 Viewport height changed:', newHeight);
            console.log('🖥️ Fullscreen state changed:', newFullscreen);
          }
        }, 200); // Increased throttle to 200ms
      };
      
      // Listen for theme changes (also throttled)
      const handleThemeChanged = () => {
        clearTimeout(themeChangeTimeout);
        themeChangeTimeout = setTimeout(() => {
          const newTheme = tgWebApp.colorScheme || 'light';
          setTheme(newTheme);
          document.documentElement.setAttribute('data-telegram-theme', newTheme);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🎨 Theme changed:', newTheme);
          }
        }, 200); // Throttle theme changes too
      };
      
      // Add event listeners
      tgWebApp.onEvent('viewportChanged', handleViewportChanged);
      tgWebApp.onEvent('themeChanged', handleThemeChanged);
      
      // Set initial CSS properties
      document.documentElement.style.setProperty('--tg-viewport-height', `${tgWebApp.viewportHeight || window.innerHeight}px`);
      document.documentElement.setAttribute('data-telegram-theme', tgWebApp.colorScheme || 'light');        // Cleanup function
      return () => {
        try {
          clearTimeout(viewportChangeTimeout);
          clearTimeout(themeChangeTimeout);
          tgWebApp.offEvent('viewportChanged', handleViewportChanged);
          tgWebApp.offEvent('themeChanged', handleThemeChanged);
        } catch (error) {
          console.log('Cleanup error (non-critical):', error);
        }
      };
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('📱 Running outside Telegram, using fallback values');
      }
      setViewportHeight(window.innerHeight);
      
      // Set fallback CSS property
      document.documentElement.style.setProperty('--tg-viewport-height', `${window.innerHeight}px`);
    }
  }, []);

  // Helper functions
  const showMainButton = (text, onClick) => {
    if (webApp && webApp.MainButton) {
      webApp.MainButton.setText(text);
      webApp.MainButton.onClick(onClick);
      webApp.MainButton.show();
    }
  };

  const hideMainButton = () => {
    if (webApp && webApp.MainButton) {
      webApp.MainButton.hide();
    }
  };

  const showBackButton = (onClick) => {
    if (webApp && webApp.BackButton) {
      webApp.BackButton.onClick(onClick);
      webApp.BackButton.show();
    }
  };

  const hideBackButton = () => {
    if (webApp && webApp.BackButton) {
      webApp.BackButton.hide();
    }
  };

  const hapticFeedback = (type = 'impact', style = 'medium') => {
    if (webApp && webApp.HapticFeedback) {
      if (type === 'impact') {
        webApp.HapticFeedback.impactOccurred(style);
      } else if (type === 'notification') {
        webApp.HapticFeedback.notificationOccurred(style);
      } else if (type === 'selection') {
        webApp.HapticFeedback.selectionChanged();
      }
    }
  };

  const openLink = (url, options = {}) => {
    if (webApp) {
      webApp.openLink(url, options);
    } else {
      window.open(url, '_blank');
    }
  };
  const close = () => {
    if (webApp) {
      webApp.close();
    }
  };

  const requestFullscreen = () => {
    if (webApp && webApp.requestFullscreen && typeof webApp.requestFullscreen === 'function') {
      webApp.requestFullscreen();
      console.log('🖥️ Requesting fullscreen mode');
    } else {
      console.log('⚠️ Fullscreen not supported in this environment');
    }
  };

  const exitFullscreen = () => {
    if (webApp && webApp.exitFullscreen && typeof webApp.exitFullscreen === 'function') {
      webApp.exitFullscreen();
      console.log('🖥️ Exiting fullscreen mode');
    } else {
      console.log('⚠️ Exit fullscreen not supported in this environment');
    }
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      exitFullscreen();
    } else {
      requestFullscreen();
    }
  };
  return {
    webApp,
    isExpanded,
    isFullscreen,
    viewportHeight,
    theme,
    showMainButton,
    hideMainButton,
    showBackButton,
    hideBackButton,
    hapticFeedback,
    openLink,
    close,
    requestFullscreen,
    exitFullscreen,
    toggleFullscreen,
    isAvailable: !!webApp
  };
};

export default useTelegramWebApp;
